import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import type { ApiInvitation, TemplateElement, TemplateLayoutSchema, TemplateSection, TemplateThemeConfig } from '@/src/types';
import { cn } from '@/src/infrastructure/lib/utils';
import { componentRegistry } from './component-registry';
import { Watermark } from '../components/Watermark';
import { isTruthyCondition } from './data-injector';
import { resolveTheme, TemplateContextProvider } from './template-context';
import { isDevEnvironment, isSupportedSectionType, reportUnknownNode } from './template-registry';

type Props = {
  schema: TemplateLayoutSchema;
  data: ApiInvitation;
  theme?: TemplateThemeConfig | null;
  mode?: 'preview' | 'live';
  hasPartnerAttribution?: boolean;
};

function sectionClass(type: string) {
  switch (type) {
    case 'hero':
      return 'px-8 py-16 text-center space-y-5';
    case 'media':
      return 'px-6 pb-6';
    case 'details':
      return 'px-8 py-8 space-y-4';
    case 'message':
      return 'px-8 py-4';
    case 'gallery':
      return 'px-8 py-6';
    case 'rsvp':
      return 'px-8 py-8 space-y-4 text-center';
    case 'blessing':
      return 'px-8 py-12 text-center space-y-4';
    case 'names':
      return 'px-8 py-10 flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-center';
    case 'headline':
      return 'px-8 py-6 text-center';
    case 'muhurta':
      return 'px-8 py-8 text-center';
    case 'venues':
      return 'px-8 py-8 space-y-4';
    case 'ceremonies':
      return 'px-8 py-8 space-y-4';
    default:
      return 'px-8 py-6';
  }
}

/**
 * Optional decorative background for a section's `layers` (overlay colour).
 * Lagna Patrika sections declare a base overlay colour; honouring it keeps the
 * preview faithful. Unknown layer kinds are ignored, never errored.
 */
function sectionBackground(section: TemplateSection): React.CSSProperties | undefined {
  const layers = (section as { layers?: Array<{ type?: string; color?: string }> }).layers;
  if (!Array.isArray(layers)) return undefined;
  const overlay = layers.find((layer) => layer?.type === 'overlay' && typeof layer.color === 'string');
  return overlay ? { backgroundColor: overlay.color } : undefined;
}

/**
 * Renders one element node. If no renderer is registered for the type we follow
 * the graceful-fallback contract: log + monitor, show a neutral placeholder in
 * development ONLY, and render nothing (never an error banner) for end users.
 */
function NodeRenderer({ node }: { node: TemplateElement; key?: React.Key }) {
  const Component = componentRegistry.resolve(node.type);
  if (!Component) {
    reportUnknownNode('element', node.type);
    if (isDevEnvironment()) {
      return (
        <div
          data-testid="template-node-placeholder"
          className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-3 text-xs text-stone-400"
        >
          {node.type} (preview unavailable)
        </div>
      );
    }
    return null;
  }

  return (
    <Component node={node}>
      {node.children?.map((child) => <NodeRenderer key={child.id} node={child} />)}
    </Component>
  );
}

function SectionRenderer({ section, data }: { section: TemplateSection; data: ApiInvitation; key?: React.Key }) {
  if (!isTruthyCondition(section.condition, data)) return null;

  // An unknown section type still renders its children inside the generic
  // container — we just record it for monitoring. Sections never show an error.
  if (!isSupportedSectionType(section.type)) {
    reportUnknownNode('section', section.type);
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      className={cn(sectionClass(section.type))}
      style={sectionBackground(section)}
      data-section-type={section.type}
    >
      {section.children?.map((node) => <NodeRenderer key={node.id} node={node} />)}
    </motion.section>
  );
}

export function JsonTemplateRenderer({ schema, data, theme, mode = 'live', hasPartnerAttribution }: Props) {
  const tokens = useMemo(() => resolveTheme(theme), [theme]);
  const sections = useMemo(
    () => [...schema.sections].sort((a, b) => a.order - b.order),
    [schema.sections],
  );

  return (
    <TemplateContextProvider value={{ data, tokens, mode }}>
      <main
        className="min-h-screen px-4 py-8 sm:py-12"
        style={{
          background: `linear-gradient(180deg, ${tokens.color.background}, ${tokens.color.accent}55)`,
          color: tokens.color.text,
          fontFamily: tokens.font.sans,
        }}
      >
        <article
          className="mx-auto w-full max-w-2xl overflow-hidden shadow-2xl"
          style={{
            backgroundColor: tokens.color.surface,
            borderRadius: tokens.radius.lg,
            border: `1px solid ${tokens.color.border}`,
          }}
        >
          <div
            className="h-2"
            style={{
              background: `linear-gradient(90deg, ${tokens.color.primary}, ${tokens.color.secondary}, ${tokens.color.primary})`,
            }}
          />
          {sections.map((section) => (
            <SectionRenderer key={section.id} section={section} data={data} />
          ))}
          <Watermark isPremium={data.isPremium} hasPartnerAttribution={hasPartnerAttribution} />
        </article>
      </main>
    </TemplateContextProvider>
  );
}
